/**
 * Password Management Application Service
 * 
 * Handles password changes, resets, and recovery
 * Separate service for password-related use cases
 */
import { PersonAuth } from '../../domain/aggregates/PersonAuth';
import type { IPersonAuthRepository } from '../../domain/repositories/IPersonAuthRepository';
import type { IAuthSessionRepository } from '../../domain/repositories/IAuthSessionRepository';
import { v4 as uuidv4 } from 'uuid';

export interface ChangePasswordCommand {
  personAuthId: string;
  currentPassword: string;
  newPassword: string;
}

export interface ResetPasswordCommand {
  email: string;
}

export interface CompleteResetCommand {
  token: string;
  newPassword: string;
}

export interface AdminResetCommand {
  personAuthId: string;
  newPassword: string;
  adminId: string;
  reason: string;
}

export class PasswordService {
  private resetTokens = new Map<string, { personAuthId: string; expiresAt: Date }>();

  constructor(
    private personAuthRepo: IPersonAuthRepository,
    private sessionRepo: IAuthSessionRepository,
    private eventBus: { publish: (event: any) => Promise<void> },
    private emailService: { send: (email: any) => Promise<void> }
  ) {}

  /**
   * User-initiated password change
   */
  async changePassword(command: ChangePasswordCommand): Promise<boolean> {
    const personAuth = await this.personAuthRepo.findById(command.personAuthId);
    if (!personAuth) return false;

    // Delegate to domain
    const success = await personAuth.changePassword(
      command.currentPassword,
      command.newPassword
    );

    if (success) {
      await this.personAuthRepo.save(personAuth);
      await this.publishEvents(personAuth);
      await this.terminateAllSessions(personAuth.getPersonId());
    }

    return success;
  }

  /**
   * Request password reset via email
   */
  async requestPasswordReset(command: ResetPasswordCommand): Promise<boolean> {
    const personAuth = await this.personAuthRepo.findByEmail(command.email);
    if (!personAuth) {
      // Don't reveal if email exists
      return true;
    }

    // Generate reset token
    const token = uuidv4();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 1); // 1 hour expiry

    // Store token (in production, use persistent storage)
    this.resetTokens.set(token, {
      personAuthId: personAuth.getId(),
      expiresAt
    });

    // Send email
    await this.emailService.send({
      to: command.email,
      subject: 'Password Reset Request',
      template: 'password-reset',
      data: { token, expiresAt }
    });

    return true;
  }

  /**
   * Complete password reset with token
   */
  async completePasswordReset(command: CompleteResetCommand): Promise<boolean> {
    // Validate token
    const tokenData = this.resetTokens.get(command.token);
    if (!tokenData || tokenData.expiresAt < new Date()) {
      return false;
    }

    // Get aggregate
    const personAuth = await this.personAuthRepo.findById(tokenData.personAuthId);
    if (!personAuth) return false;

    // Reset password
    await personAuth.resetPasswordByAdmin(command.newPassword);
    await this.personAuthRepo.save(personAuth);
    await this.publishEvents(personAuth);

    // Clean up token
    this.resetTokens.delete(command.token);

    // Terminate all sessions
    await this.terminateAllSessions(personAuth.getPersonId());

    return true;
  }

  /**
   * Admin-initiated password reset
   */
  async adminResetPassword(command: AdminResetCommand): Promise<boolean> {
    const personAuth = await this.personAuthRepo.findById(command.personAuthId);
    if (!personAuth) return false;

    await personAuth.resetPasswordByAdmin(command.newPassword);
    await this.personAuthRepo.save(personAuth);
    await this.publishEvents(personAuth);
    await this.terminateAllSessions(personAuth.getPersonId());

    // Audit log would be handled via domain events
    return true;
  }

  private async terminateAllSessions(personId: string): Promise<void> {
    const sessions = await this.sessionRepo.findByPersonId(personId);
    for (const session of sessions) {
      session.invalidate();
      await this.sessionRepo.save(session);
    }
  }

  private async publishEvents(aggregate: PersonAuth): Promise<void> {
    const events = aggregate.getUncommittedEvents();
    for (const event of events) {
      await this.eventBus.publish(event);
    }
    aggregate.markEventsAsCommitted();
  }
}